import { enLongTail } from "./en-long-tail";
import { enMantras } from "./en-mantras";
import { enVrat } from "./en-vrat";
export interface PageMeta {
  title: string;
  description: string;
  ogTitle?: string;
  ogDescription?: string;
}

const en: Record<string, PageMeta> = {
  panchang: {
    title: "Aaj Ka Panchang — Tithi, Shubh Muhurat & Rahu Kaal Today | Ishvaram",
    description:
      "Aaj Ka Panchang — today's Tithi, Nakshatra, Yoga, Karana, and Rahu Kaal. Free daily Panchang with accurate planetary positions and auspicious timings for your city.",
    ogTitle: "Today's Panchang | Aaj Ka Panchang | Ishvaram",
    ogDescription:
      "Aaj Ka Panchang — today's Tithi, Nakshatra, Yoga, Karana with accurate timings. 100% free.",
  },
  rashifal: {
    title: "Aaj Ka Rashifal Today — 12 Rashi Daily Horoscope | Ishvaram",
    description:
      "Aaj ka rashifal for all 12 rashis, Mesh to Meen, from today's planetary transits, in Hindi and English.",
    ogTitle: "Aaj Ka Rashifal — Today's 12 Rashi Horoscope | Ishvaram",
    ogDescription:
      "Free daily rashifal for all 12 rashi. Live planetary transits, Hindi + English.",
  },
  "free-kundali": {
    title: "Free Kundali Online by Date of Birth | Ishvaram",
    description:
      "Generate your free kundali online by date of birth. Explore your chart and Vedic reading; without birth time, get a reduced reading with clear limits.",
    ogTitle: "Free Kundali Online by Date of Birth | Ishvaram",
    ogDescription:
      "Free kundli with a Vedic explanation. Exact birth time supports houses and Dasha timing; unknown time gives a reduced reading.",
  },
  "kundli-matching": {
    // 74 chars truncated on mobile (86% of impressions). Pre-cliff GSC
    // (2026-06-15..07-20): "kundali matching" 68.7K impr @ 1.25% CTR pos 7.9,
    // "kundli matching" 43.5K @ 1.24% — against the ~2.7% the position should
    // earn. Same head terms, inside the 60-char SERP budget.
    // "gun milan by name" 15K impr + "kundali milan by name" 8.2K hit this
    // page; the naam-akshar mode shipped 2026-08-09 (NaamMatchingMode), so the
    // snippet may claim it honestly — the old "no name-only match" denial was
    // repelling exactly those clicks. Pinned by seo-metadata.test.ts.
    // 2026-09-30 (SEO audit w3-matching): the by-name intent moved to its one
    // owner, /gun-milan/; this page matches from birth details only and links
    // there, so the snippet no longer claims a name mode it does not host.
    title: "Free Kundali Matching for Marriage — 36 Gun Milan | Ishvaram",
    description:
      "Free kundali matching for marriage from both birth details: 36-point Gun Milan with all 8 kootas, South Indian 10 porutham, Mangal and Nadi Dosh checks.",
    ogTitle: "Kundli Matching — 36-Point Gun Milan in 60s | Ishvaram",
    ogDescription:
      "Free 36-point Gun Milan + Mangal Dosha + Nadi dosha — marriage compatibility verdict in seconds.",
  },
  "ai-astrologer": {
    title: "Free AI Astrologer Online — Instant Kundli & Astrology Chat | Ishvaram",
    description:
      "Chat with an AI Vedic Astrologer online for free. Swiss Ephemeris kundali and first answer included; see price and duration before paid continuation.",
    ogTitle: "Free AI Astrologer Online & Kundli Chat | Ishvaram",
    ogDescription:
      "Free kundali and first AI Jyotishi answer, in your browser. Explore calculated chart evidence and traditional upay; further consultation may be paid.",
  },
  "celebrity-kundali": {
    title: "Celebrity Kundali & Vedic Birth Charts | Ishvaram",
    description:
      "Explore accurate Vedic birth charts, Lagna, Rashi, and Raj Yogas of notable personalities across politics, sports, cinema, and business.",
  },
  astrologers: {
    title: "AI Astrologer Online — Free Vedic AI Jyotishi | Ishvaram",
    description:
      "Meet Ishvaram's AI Vedic astrologer — grounded in Parashari Jyotish, it reads your full chart and answers any question. Free, 24/7, no queue.",
    ogTitle: "Free AI Astrologer — Talk Now | Ishvaram",
    ogDescription:
      "Your Vedic AI astrologer — Parashari Jyotish, full chart reading. Free chat, 24/7, your chart loaded.",
  },
  pandits: {
    title: "AI Pandit Ji Online | Free Vedic Remedies & Muhurat | Ishvaram",
    description:
      "Consult AI Pandit Ji online for free — dosha remedies, shubh muhurat, puja guidance, classical Vedic shastra. Trained in the Kashi Parashari parampara.",
    ogTitle: "AI Pandit Ji — Free Remedies + Muhurat | Ishvaram",
    ogDescription:
      "Consult AI Pandit Ji for dosha nivaran, shubh muhurat, and classical Vedic guidance. Free.",
  },
  "astrologer-for-marriage-problem": {
    title: "Astrologer for Marriage Problem | Free AI Jyotishi Reads 7th House | Ishvaram",
    description:
      "Talk to the AI Jyotishi free about a marriage problem: it reads your 7th house, Shukra and Mangal Dosh and suggests an upay. No per-minute charges.",
    ogTitle: "Astrologer for Marriage Problem — Free 7th House Read | Ishvaram",
    ogDescription:
      "Free AI Jyotishi reads your 7th house, Shukra, and Mangal Dosh behind a marriage problem, with the upay.",
  },
  "career-astrology-consultation": {
    title: "Career Astrology Consultation Free | AI Jyotishi Reads 10th House | Ishvaram",
    description:
      "Free career astrology consultation — the AI Jyotishi reads your 10th house, D10, running dasha and Shani/Guru transits, and names the upay.",
    ogTitle: "Career Astrology Consultation Free — 10th House Read | Ishvaram",
    ogDescription:
      "Free career consultation — AI Jyotishi reads your 10th house, dasha, and transits, with the career upay.",
  },
  choghadiya: {
    // Rule 76 (frozen registry 2026-08-09: 12,195 impr, earned 14.1% of par):
    // 71 chars truncated on mobile, and TIME_SENSITIVE_SLUGS already prefixes
    // the date — "Today" + the brand tail were pure truncation.
    title: "Aaj Ka Choghadiya — Live Shubh Muhurat for Your City",
    description:
      "Today's Choghadiya — Amrit, Shubh, Labh, Char times auto-detected for your city (Delhi, Mumbai, Bengaluru + 200 cities). Day & night muhurat table updated live at sunrise. Free, no login.",
  },
  "shubh-muhurat": {
    title: "Shubh Muhurat Today — Good Time Now? | Ishvaram",
    description:
      "Is now a good time? Check today's city-specific Shubh Muhurat, Abhijit Muhurta, favorable Choghadiya and Rahu Kaal to avoid across India.",
  },
  "rahu-kaal": {
    // Rule 76 snippet fix (frozen registry 2026-08-09: 47,242 impr / 201 clicks,
    // earned 11.6% of par). Google shows a generic Rahu Kaal in the answer box;
    // the snippet has to promise the thing the box cannot — the window computed
    // from YOUR city's sunrise, plus the two sibling windows. Pipe-list title
    // repeated the same head twice and differentiated nothing.
    title: "Rahu Kaal Today — Exact Time for Your City",
    description:
      "Rahu Kaal start and end computed from your city's sunrise — not a fixed table. Yamagandam and Gulika Kaal too: know exactly when not to start new work. Free.",
  },
  muhurat: {
    // Rule 76: 62 chars, so "| Ishvaram" and half the value prop truncated on
    // mobile. Top queries are "shubh muhurt" (1,350 impr), "auspicious time"
    // (197) and "shubh muhurat this month" (124) — a month-scoped date lookup.
    title: "Shubh Muhurat {year}: Auspicious Dates by Event & Month",
    description:
      "Shubh muhurat dates for {year} — marriage, griha pravesh, vehicle, business, mundan and 10 more events, month by month, with Nakshatra and Tithi. Free.",
  },
  numerology: {
    // Dominant queries (GSC 28d to 2026-07-20): "numerology calculator" (1,790
    // impr, pos 10.5), "namank calculator" (893), "vedic numerology calculator"
    // (543). Title now fits the 60-char SERP budget and keeps all three heads.
    title: "Numerology Calculator — Mulank, Bhagyank, Namank | Ishvaram",
    description:
      "Free Vedic numerology calculator: enter your date of birth and name to get your Mulank, Bhagyank and Namank, each number's ruling planet, and the upay.",
  },
  "name-numerology": {
    title: "Name Numerology Calculator | Find Your Name Number | Ishvaram",
    description:
      "Free Name Numerology Calculator — enter your name to find your Name Number (1-9).",
  },
  "mulank-calculator": {
    // Rule 76: 75 chars truncated everything after "Birth" on mobile, where 86%
    // of impressions are. Same promise, inside the 60-char SERP budget.
    title: "Mulank Calculator — Root Number by Date of Birth (Free)",
    description:
      "Mulank is the root number of your birth day (1-9). Enter your date of birth to see your Mulank, its ruling graha and the upay for it.",
  },
  "bhagyank-calculator": {
    title: "Bhagyank Calculator — Find Your Destiny Number by Date of Birth (Free) | Ishvaram",
    description:
      "Bhagyank is the destiny number from your full date of birth (1-9). Find yours, its ruling planet and the upay for it.",
  },
  "baby-names": {
    title: "Baby Names by Nakshatra | Numerology Score | Ishvaram",
    description:
      "Find the perfect baby name by nakshatra and numerology. 1500+ authentic Sanskrit names with Hindi meanings, starting syllables, and numerology scores.",
  },
  "ascendant-calculator": {
    // Rule 76 (frozen registry 2026-08-09: 40,337 impr, earned 14.8% of par):
    // 67 chars truncated the tail on mobile. Inside the 60-char budget.
    title: "Lagna Calculator — Your Ascendant by Birth Time (Free)",
    description:
      "Free Lagna calculator: enter birth date, exact time and birthplace to find your Vedic ascendant sign, Lagna degree, Moon sign context and 1st house.",
    ogTitle: "Lagna Calculator — Free Ascendant Sign by Birth Date | Ishvaram",
    ogDescription:
      "Find your exact Lagna + lord + nakshatra pada in 30 seconds. Accurate Vedic calculations.",
  },
  "nakshatra-calculator": {
    title: "Free Nakshatra Calculator | Birth Star Finder | Ishvaram",
    description:
      "Find your Nakshatra (birth star) and pada by date of birth. 100% free — get your Janma Nakshatra, ruling planet, deity, and name syllables.",
  },
  "rashi-calculator": {
    // Dominant queries (GSC 28d to 2026-07-20) are question-shaped: "how to
    // know my rashi" (268 impr), "what is my rashi by date of birth" (186),
    // "how to know rashi by date of birth without time" (94).
    // 2026-08-12: the DATE-ONLY MODE shipped (RashiDateOnlyMode — the rule 29 #5
    // three-state manual mode: it shows what the date settles, or asks). The
    // snippet may name that mode, but must never claim nakshatra/pada/lagna or
    // "birth time optional" — the exact mode still hard-requires the instant
    // (toExactRashiRequest throws). Pinned by rashi-birth-time-parity.test.ts.
    title: "Rashi Calculator — Moon Sign by Date of Birth | Ishvaram",
    description:
      "Find your Rashi (Moon sign) free: the exact mode reads your birth date, time and place; the date-only mode shows what your birth date alone settles.",
  },
  "mangal-dosha-calculator": {
    // Rule 76: 70 chars, so the differentiator ("by Date of Birth") truncated.
    // The queries are question-shaped — "how to know if i am manglik" 207 impr
    // (5 clicks), "am i manglik or not", "manglik check" — so the title asks the
    // seeker's exact question back and answers it in the description.
    title: "Am I Manglik? Free Mangal Dosh Check by Date of Birth",
    description:
      "Find out if you are Manglik from your birth date, time and place. See the Mangal Dosh intensity, whether it cancels out, and the upay for it.",
  },
  "pitra-dosh-calculator": {
    title: "Pitra Dosh Calculator | Free Pitra Dosha Check | Ishvaram",
    description:
      "Check Pitra Dosh (पितृ दोष) in your birth chart free: Sun and node afflictions, severity, and tarpan, shraddh and Vedic upay.",
  },
  "dasha-calculator": {
    // This entry became the live owner on 2026-08-12: the route's layout.tsx
    // used to hardcode its own Metadata with a bare canonical and NO hreflang
    // languages, while /hi/dasha-calculator/ annotated this page — a
    // half-paired hreflang cluster. page.tsx now reads this catalog entry, so
    // both locales pair through generateAlternates.
    // Rule 76 snippet fix (frozen registry 2026-08-09: 59,444 impr / 258
    // clicks, earned 8.5% of par — the worst earner on the protected list).
    // The 80-char title truncated at "Date of Bi…" on mobile; the question
    // form mirrors the worry queries that convert 20-100x better on the
    // mahadasha family ("which dasha am I running").
    title: "Dasha Calculator — Which Mahadasha Is Running? (Free)",
    description:
      "Find your running Mahadasha and Antardasha from your date of birth: period dates, what each dasha brings for career, money and marriage, and upay.",
  },
  "yogini-dasha": {
    title: "Free Yogini Dasha Calculator (36-Yr Cycle) | Ishvaram",
    description:
      "Calculate your Yogini Dasha sequence, starting Yogini, and active periods from your birth chart based on Brihat Parashara Hora Shastra Chapter 47.",
  },
  "birth-time-rectification": {
    title: "Birth Time Rectification — Exact Minute Finder | Ishvaram",
    description:
      "Don't know your exact birth minute? Enter a birth window and major life events to estimate your birth minute, Lagna and D9 Navamsha.",
  },
  "tara-balam": {
    title: "Tara Balam Calculator — Daily Nakshatra Timing | Ishvaram",
    description:
      "Calculate your personal Tara Balam from your birth star against today's transit Nakshatra.",
  },
  widgets: {
    title: "Free Vedic Astrology Widgets for Websites | Ishvaram",
    description:
      "Embed free, real-time Vedic Panchang, Choghadiya, and Shubh Muhurat widgets on your website or blog. Fast, responsive, dark/light themes, and 100% free.",
  },
  "gun-milan": {
    // Pre-cliff GSC (28d to 2026-07-20): "gun milan by name" is this page's
    // TOP query — 6.5K impr @ 1.28% pos 7.4 — and the naam-akshar mode now
    // renders on this route (GunMilanClient, 2026-08-12), so the snippet may
    // claim it honestly. Bilingual page; the en snippet carries the Latin
    // head terms.
    // 2026-09-30 (SEO audit w3-matching): this route is the ONE by-name owner
    // (the intent was split across four URLs pre-drop), so the name mode is the
    // first screen and "by Name" leads the title and H1. /kundli-matching/
    // links here instead of hosting a second name mode.
    title: "Gun Milan by Name or Birth Details — 36 Ashtakoot Calculator | Ishvaram",
    description:
      "Free Gun Milan by name: match from the naam-akshar when the birth time is unknown, or from both birth details for the full 36-point Ashtakoot score with all 8 kootas, Mangal Dosh and Nadi Dosh checks.",
  },
  "tithi-today": {
    // Rule 76 snippet fix — the largest FIXABLE entry in the frozen registry
    // (2026-08-09: 185,377 impr / 1,472 clicks, earned 22% of par; the Hindi
    // twin adds 433,701 impr). Google shows the tithi NAME in the answer box;
    // the snippet promises what the box cannot: the exact end time and the
    // shubh/ashubh verdict for the seeker's plan. TIME_SENSITIVE_SLUGS
    // prefixes today's IST date, so the brand tail was pure truncation.
    title: "Aaj Ki Tithi — Today's Tithi, Paksha, End Time & Muhurat",
    description:
      "Aaj ki tithi with its exact end time — Paksha, the next Tithi, and whether today is shubh for the work you're planning. Accurate Vedic calculation, free.",
    ogTitle: "Aaj Ki Tithi — Today's Hindu Tithi | Ishvaram",
    ogDescription:
      "Today's Tithi, Paksha, and significance. Accurate Hindu lunar calendar day — free.",
  },
  "shani-transit": {
    title: "Shani Transit {prevyear}-{year} | Saturn Current Position & Affected Rashis | Ishvaram",
    description:
      "Saturn (Shani) current transit position, which Moon signs are affected, Sade Sati status {prevyear}-{year}. Check how Shani Gochar impacts your rashi — free.",
    ogTitle: "Shani Transit {prevyear}-{year} | Saturn Position & Affected Rashis",
    ogDescription:
      "Saturn's current rashi, degree, and which Moon signs are under Sade Sati right now. Free transit check.",
  },
  "sade-sati": {
    // Rule 76: 84 chars — the whole hook truncated away on mobile, so a searcher
    // saw "Sade Sati Calculator — Why Life Feels Stuck Right N…". After the bare
    // noun ("sade sati" 370 impr, "sadesati" 236) the dominant intent is the
    // per-rashi window: "kumbh rashi sade sati start and end date" 190 impr (2
    // clicks), "sade sati end for kumbh rashi english" 149 (2). The title now
    // promises the phase and its dates; the rule-22 pain-question hook moves
    // into the description, where it survives the 170-char budget intact.
    title: "Sade Sati Calculator: Your Phase, Start & End Dates",
    description:
      "Why does life feel uphill lately? Sade Sati is Shani's 7.5-year transit over your Moon sign: check your phase, its dates and the upay.",
  },
  transits: {
    title: "Grah Gochar {year} | Saturn & Jupiter Transit | Ishvaram",
    description:
      "Check today's planetary transits — Saturn (Shani), Jupiter (Guru), Rahu-Ketu current positions. Free Sade Sati check and transit predictions.",
  },
  remedies: {
    title: "Dosha Remedies | Personalized Vedic Astrology Remedies | Ishvaram",
    description:
      "Dosha remedies from your birth chart: detect Mangal, Kaal Sarp and Shani Dosha, then get the matched mantras, remedies and gemstone guidance.",
  },
  compatibility: {
    title: "Free Rashi Compatibility & Gun Milan Calculator | Ishvaram",
    description:
      "Check Vedic rashi compatibility across all 144 zodiac sign pairs. Free 36-point Gun Milan, elemental harmony, and marriage astrology analysis.",
  },

  "ai-kundli": {
    title: "Free AI Kundli | AI-Powered Janam Kundali Online | Ishvaram",
    description:
      "Generate your Janam Kundali with the AI Kundli calculator: planet positions, house meanings and an explained reading, free.",
    ogTitle: "Free AI Kundli | AI-Powered Janam Kundali Online | Ishvaram",
    ogDescription: "Generate your Janam Kundali instantly with our AI Kundli calculator.",
  },
  "birth-chart": {
    title: "Free Vedic Birth Chart Calculator | Janam Kundali | Ishvaram",
    description:
      "Calculate your Vedic birth chart instantly. Get accurate planetary positions, house analysis, and detailed kundali interpretation\u2014100% free.",
  },
  calendar: {
    title: "Hindu Calendar {year}-{nextyear} | Festival & Vrat Dates | Ishvaram",
    description:
      "Hindu Calendar {year}-{nextyear} with festival dates, vrat (fasting) schedules, auspicious timings, and rituals for all Hindu holidays.",
  },
  dosha: {
    title: "Doshas in Vedic Astrology | Mangal Dosha, Kaal Sarp & More | Ishvaram",
    description:
      "Understand Doshas in Vedic astrology including Mangal Dosha, Kaal Sarp Dosha, Pitra Dosha. Check your birth chart for doshas and remedies.",
  },
  graha: {
    // Rule 76: 65 chars, so "Navagraha Guide | Ishvaram" truncated on mobile.
    // Top queries are the bare noun and its English mapping \u2014 "graha" 672 impr
    // (1 click), "grahas" 198, "9 grah name" 84, "9 graha name" 83, "graha in
    // english" 64 \u2014 so the title answers "what are the 9 grahas called".
    title: "9 Grahas (Navagraha): Names in English & Effects",
    description:
      "Learn about the 9 Grahas (planets) in Vedic astrology: Sun, Moon, Mars, Mercury, Jupiter, Venus, Saturn, Rahu & Ketu.",
  },
  "kaal-sarp-dosh": {
    title: "Kaal Sarp Dosh Calculator — Check Your Kundali Free | Ishvaram",
    description:
      "Is Kaal Sarp Dosh in your birth chart? Enter your birth details for a free, instant check — engine-computed from your planetary positions.",
    ogTitle: "Kaal Sarp Dosh Calculator — Free Check | Ishvaram",
    ogDescription:
      "Find out if all planets in your kundali are trapped between Rahu and Ketu. Free Kaal Sarp Dosh check with sub-type and upay.",
  },
  "kaal-sarp-dosha": {
    title: "Kaal Sarp Dosh — Why Success Always Slips Away (12 Types, Symptoms & Upay) | Ishvaram",
    description:
      "Kaal Sarp Dosh forms when all seven planets sit on one side of the Rahu-Ketu axis. Check your chart free, find your type and the classical upay.",
  },
  "lal-kitab": {
    title: "Lal Kitab | Free Reading, Remedies & Rin Analysis | Ishvaram",
    description:
      "Free Lal Kitab reading online — house-based planetary analysis, Rin (karmic debt) detection, and authentic upay (remedies) for every planet.",
    ogTitle: "Free Lal Kitab Reading — House-Based Remedies | Ishvaram",
    ogDescription:
      "Authentic Lal Kitab analysis: every planet, every house, every upay. Free Kundali + Rin check + remedies.",
  },
  "pitra-dosha": {
    title: "Pitra Dosh — Why Misfortune Follows Your Family (Symptoms & Upay) | Ishvaram",
    description:
      "Pitra Dosh check: the Sun, 9th house and Rahu/Ketu afflictions that point to ancestral karmic debt, with tarpan and shraddh upay.",
    ogTitle: "Pitra Dosh — Ancestral Karmic Debt & Upay | Ishvaram",
    ogDescription:
      "Detect Pitra Dosha in your chart. Understand symptoms. Perform the right tarpan + upay. Free Vedic analysis.",
  },
  upay: {
    title: "Upay — Vedic Astrology Remedies | Mantra, Daan, Gemstone Upay | Ishvaram",
    description:
      "Upay in Vedic astrology — mantra, daan, vrat, gemstone and sacred-item remedies for Shani, Rahu, Mangal, Ketu, Kaal Sarp and Pitra Dosh.",
    ogTitle: "Upay — Vedic Astrology Remedies for Doshas | Ishvaram",
    ogDescription:
      "Mantra, daan, vrat, gemstone & sacred item upay for Shani, Rahu, Mangal, Ketu & all doshas. Find the right upay for your chart.",
  },
  ashtakvarga: {
    title: "Ashtakvarga — Sarvashtakvarga Bindu Chart | Ishvaram",
    description:
      "Ashtakvarga (अष्टकवर्ग) system: the Vedic bindu-based method for measuring planetary strength in every house.",
    ogTitle: "Ashtakvarga — Planetary Strength Chart | Ishvaram",
    ogDescription:
      "Bindu-by-bindu planetary strength reading. Free Sarvashtakvarga table and transit analysis.",
  },
  varshphal: {
    // The live page's title, kept. The description is the one deliberate head
    // change (owner decision 2026-10-09): it names what the page's chart casts
    // and nothing it does not (no Muddha dasha, yogas or "free analysis"), in
    // 153 visible characters. og:description follows it. Pinned, with the
    // reasons, in src/app/varshphal/page.test.tsx; it reaches readers only when
    // the D1 row is flipped to app:varshphal.
    title: "Varshphal | Free Solar Return Yearly Horoscope (Tajik)",
    description:
      "Varshphal (वर्षफल): your Tajik solar-return chart for this year — Varsha Lagna, Muntha and when your varsha begins and ends. Free from your birth details.",
  },
  "kp-astrology": {
    title: "KP Astrology | Krishnamurti Paddhati Sub-Lord System | Ishvaram",
    description:
      "KP astrology (Krishnamurti Paddhati) — the stellar sub-lord system for precise event timing.",
    ogTitle: "KP Astrology — Sub-Lord System Reading | Ishvaram",
    ogDescription:
      "Precise event timing via KP sub-lords. Free Krishnamurti Paddhati chart + reading.",
  },
  vastu: {
    title: "Vastu Shastra | Free Analysis, Room Placement & Remedies | Ishvaram",
    description:
      "Free Vastu Shastra analysis — room placement by direction, common doshas, traditional remedies, and 9x9 Vastu Purusha Mandala guidance.",
    ogTitle: "Vastu Shastra — Free Directional Analysis | Ishvaram",
    ogDescription:
      "Directional Vastu analysis + remedies. Classical 9x9 Mandala principles, free and instant.",
  },
  "monthly-rashifal": {
    title: "Monthly Rashifal — Masik Horoscope for 12 Rashi | Ishvaram",
    description:
      "Masik Rashifal: the month ahead for all 12 rashis \u2014 career, health, love, money and the key dates.",
  },
  nakshatra: {
    title: "27 Nakshatras — Vedic Birth Star Guide | Ishvaram",
    description:
      "All 27 Nakshatras in Vedic astrology — ruling planets, personality traits, career paths, marriage compatibility and Vedic remedies.",
  },
  "nakshatra/chitra-female": {
    title: "Chitra Nakshatra Female \u2014 Personality, Career, Marriage & Compatibility",
    description:
      "Chitra Nakshatra female personality, career prospects, marriage compatibility, and life predictions. Understand traits and best matches for Chitra females.",
  },
  "nakshatra/purva-ashadha": {
    title: "Purva Ashadha Nakshatra \u2014 Personality, Career, Marriage & Compatibility",
    description:
      "Purva Ashadha Nakshatra personality, career, marriage compatibility, and life predictions. Learn about traits, strength and best matches.",
  },
  "nakshatra/purva-phalguni": {
    title: "Purva Phalguni Nakshatra \u2014 Personality, Career, Marriage & Compatibility",
    description:
      "Purva Phalguni Nakshatra personality, career, marriage compatibility, and life predictions. Understand personality traits and best matches.",
  },
  "nakshatra/pushya-female": {
    title: "Pushya Nakshatra Female \u2014 Personality, Career, Marriage & Compatibility",
    description:
      "Pushya Nakshatra female personality, career prospects, marriage compatibility, and life predictions.",
  },
  "nakshatra/shatabhisha-female": {
    title: "Shatabhisha Nakshatra Female \u2014 Personality, Career, Marriage & Compatibility",
    description:
      "Shatabhisha Nakshatra female personality, career prospects, marriage compatibility, and life predictions.",
  },
  "nakshatra/uttara-bhadrapada-female": {
    title:
      "Uttara Bhadrapada Nakshatra Female \u2014 Personality, Career, Marriage & Compatibility",
    description:
      "Uttara Bhadrapada Nakshatra female personality, career prospects, marriage compatibility, and life predictions.",
  },
  "nakshatra/uttara-phalguni-compatibility": {
    title: "Uttara Phalguni Nakshatra Compatibility \u2014 Best Matches & Marriage",
    description:
      "Uttara Phalguni Nakshatra compatibility guide for marriage and relationships. Find best matches and understand compatibility traits.",
  },
  "nakshatra/purva-bhadrapada": {
    title: "Purva Bhadrapada Nakshatra \u2014 Personality, Career, Marriage & Remedies | Ishvaram",
    description:
      "Complete guide to Purva Bhadrapada Nakshatra (25th nakshatra). Ruled by Jupiter in Aquarius-Pisces.",
  },
  "nakshatra/mrigashira-characteristics": {
    title:
      "Mrigashira Nakshatra Characteristics \u2014 Personality, Career & Compatibility | Ishvaram",
    description:
      "Mrigashira Nakshatra characteristics — the eternal seeker. Ruled by Mars in Taurus-Gemini.",
  },
  "nakshatra/anuradha-male": {
    title: "Anuradha Nakshatra Male \u2014 Personality, Career & Marriage | Ishvaram",
    description:
      "Anuradha Nakshatra male characteristics — devoted, disciplined, born leaders. Ruled by Saturn in Scorpio.",
  },
  "nakshatra/punarvasu-male": {
    title: "Punarvasu Nakshatra Male \u2014 Personality, Career & Marriage | Ishvaram",
    description:
      "Punarvasu Nakshatra male characteristics — optimistic, philosophical, natural teachers. Ruled by Jupiter in Gemini-Cancer.",
  },
  "nakshatra/dhanishta-male": {
    title: "Dhanishta Nakshatra Male \u2014 Personality, Career & Marriage | Ishvaram",
    description:
      "Dhanishta Nakshatra male characteristics — ambitious, musical, wealth-builders. Ruled by Mars in Capricorn-Aquarius.",
  },
  notifications: {
    title: "Notification Settings | Ishvaram Jyotish",
    description:
      "Manage your notification preferences for personalized astrology updates, predictions, and reminders.",
  },
  prashna: {
    title: "Prashna Kundali \u2014 Ask Any Question Free | Horary Astrology | Ishvaram",
    description:
      "Ask any life question and get instant answers using Prashna Kundali (horary astrology). Free prashna calculator for all your questions.",
  },
  rashi: {
    title: "All 12 Rashis in Vedic Astrology \u2014 Personality, Traits & Compatibility | Ishvaram",
    description:
      "Find your Rashi (Moon sign) free by date of birth. All 12 Rashis explained — ruling planets, traits, compatible rashis and nakshatras.",
  },
  tarot: {
    title: "Free Daily Tarot Card Reading | AI Tarot Predictions | Ishvaram",
    description:
      "Get your free daily tarot card reading and predictions. AI-powered tarot interpretation for love, career, health, and life guidance.",
  },
  mantras: {
    title: "Graha Mantra Upay — Vedic Mantras for All 9 Planets | Ishvaram",
    description:
      "Vedic mantra upay for all 9 grahas — Surya, Chandra, Mangal, Budh, Guru, Shukra, Shani, Rahu, Ketu.",
    ogTitle: "Graha Mantra Upay for All 9 Navagraha | Ishvaram",
    ogDescription:
      "Beej mantras, Vedic mantras, and jaap method for all 9 grahas. Classical Jyotish upay for Shani, Rahu, Mangal, and more.",
  },
  ...enMantras,
  ...enVrat,
  ...enLongTail,
};

export default en;
